import {
  Component,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ToastService } from '@techniek-team/services';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, share, switchMap } from 'rxjs/operators';
import { AlfredClientService } from '../alfred-client.service';
import { SessionApi } from '../api/session/session.api';
import { Session } from '../models/session.model';
import { User } from '../models/user.model';
import { QuestionnaireHandlerComponent } from '../question-handlers/questionnaire-handler/questionnaire-handler.component';
import { addIcons } from 'ionicons';
import { hourglassOutline, checkmarkOutline } from 'ionicons/icons';
import { isDefined } from '@lyceo/shared/util';
import {
  IonList,
  IonNavLink,
  IonItem,
  IonLabel,
  IonBadge,
  IonIcon,
  IonSkeletonText,
} from '@ionic/angular/standalone';
import { AsyncPipe } from '@angular/common';
import { UserApi } from '../api/user/user.api';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  imports: [
    IonList,
    IonNavLink,
    IonItem,
    IonLabel,
    IonBadge,
    IonIcon,
    IonSkeletonText,
    AsyncPipe,
  ],
})
export class OverviewComponent {
  private readonly userApi: UserApi = inject(UserApi);
  private readonly sessionApi: SessionApi = inject(SessionApi);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef =
    inject(ChangeDetectorRef);

  public alfredClientOutputService: AlfredClientService =
    inject(AlfredClientService);

  /**
   * An observable containing the sessions.
   */
  public _sessions$!: Observable<Session[]>;

  public questionnairePage: typeof QuestionnaireHandlerComponent =
    QuestionnaireHandlerComponent;

  constructor() {
    addIcons({ hourglassOutline, checkmarkOutline });
  }

  public ionViewDidEnter(): void {
    if (this.alfredClientOutputService.isApiMode()) {
      this._sessions$ = this.createSessionsObserver();
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Create an observer containing sessions.
   */
  private createSessionsObserver(): Observable<Session[]> {
    return this.userApi.getUserProfile().pipe(
      filter(isDefined),
      switchMap((user: User) => this.sessionApi.getSessions(user.id)),
      filter(isDefined),
      map((list) => list.sessions),
      catchError(() => {
        this.toastService.error(
          'Oeps! Jouw testen konden niet worden opgehaald!',
        );
        return of([]);
      }),
      share({
        connector: () => new ReplaySubject(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    );
  }
}
